<script setup lang="ts">
import { useStorage } from '@vueuse/core';
import type { AgentOnList } from '@@/shared/agent/types';
import type { IadAppRoute } from '~/application';

const props = defineProps<{
  agent: AgentOnList
}>();

const emit = defineEmits<{
  contact: [AgentOnList]
}>();

const agentPageLink = computed<IadAppRoute<`real-estate-agent-userName`>>(() => ({
  name: `real-estate-agent-userName`,
  params: { userName: props.agent.userName },
}));

const route = useRoute();
const returnTo = useStorage(`agent-${props.agent.userName}`, route.fullPath);
watch(() => route.fullPath, () => {
  returnTo.value = route.fullPath;
}, { immediate: true });

function trackNavigation() {
  trackEvent({
    event: 'agent_interaction',
    event_name: 'agent_profile_click',
    agent_id: props.agent.localId,
  });
}
</script>

<template>
  <li class="rounded-xl overflow-clip relative border border-neutral-70 flex flex-col isolate">
    <AgentAvatarLarge
      class="rounded-t-xl flex-none"
      :agent
      variant="agent-card"
    />
    <div class="p-m flex flex-col gap-s flex-1">
      <div>
        <NuxtLinkLocale
          :to="agentPageLink"
          class="font-semibold text-secondary text-xl leading-tight block"
          @click="trackNavigation"
        >
          {{ agent.fullName }}
        </NuxtLinkLocale>
        <h2 class="font-semibold block">
          {{ agent.location.place }} ({{ agent.location.postcode }})
        </h2>
      </div>
      <IadReviewSummary
        v-if="agent.reviewsCount"
        :average-rating="agent.reviewsRatingAverage"
        :reviews-count="agent.reviewsCount"
      />
      <AgentLanguages
        :agent
        class="text-sm line-clamp-[1]"
      />
      <p
        v-if="agent.propertyCount"
        class="font-semibold"
        v-text="$t(`agent.properties.available`, { count: agent.propertyCount })"
      />
      <div class="grid grid-cols-2 lg:flex gap-xs mt-auto">
        <IadBtn
          class="relative z-10 flex-1 shrink"
          size="large"
          :text="$t('core.common.send')"
          @click="emit('contact', agent)"
        />
        <AgentBtnPhone
          size="large"
          :agent
          class="relative z-10 flex-1"
        />
      </div>
    </div>
    <ClientOnly>
      <NuxtLinkLocale
        :to="agentPageLink"
        class="absolute inset-0"
        @click="trackNavigation"
      />
    </ClientOnly>
  </li>
</template>
